import { Dialog } from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AllowedDomainForm } from "../forms/AllowedDomainForm";
import {
  useAllowedDomainsStore,
  useCreateAllowedDomainStore,
  useUpdateAllowedDomainStore,
} from "../../stores/Hooks";
import { UpdateAllowedDomainRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateAllowedDomainRPC";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";

export type AllowedDomainDialogMode = "create" | "edit";

interface AllowedDomainDialogProps {
  open: boolean;
  mode: AllowedDomainDialogMode;
  onClose: VoidFunction;
  onDomainNotFound: VoidFunction;
}

export const AllowedDomainDialog: React.FC<AllowedDomainDialogProps> = observer(
  ({ open, mode, onClose, onDomainNotFound }) => {
    const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
      useState(false);

    const { t } = useTranslation();
    const translate = t as unknown as (key: string) => string;
    const allowedDomainsStore = useAllowedDomainsStore();
    const createAllowedDomainStore = useCreateAllowedDomainStore();
    const updateAllowedDomainStore = useUpdateAllowedDomainStore();

    const isCreateMode = mode === "create";
    const activeStore = isCreateMode
      ? createAllowedDomainStore
      : updateAllowedDomainStore;

    const handlePrimaryButtonClick = async (): Promise<void> => {
      try {
        if (isCreateMode) {
          await createAllowedDomainStore.createAllowedDomain();
          if (createAllowedDomainStore.disableSubmit) return;
        } else {
          await updateAllowedDomainStore.updateAllowedDomain();
          if (updateAllowedDomainStore.disableSubmit) return;

          if (
            updateAllowedDomainStore.updateAllowedDomainRPCError ===
            UpdateAllowedDomainRPC.RPCError.DomainNotFound
          ) {
            onDomainNotFound();
            return;
          }
        }

        await allowedDomainsStore.refreshCurrentPage();
        onClose();
      } catch {
        setOpenUnexpectedErrorDialog(true);
      }
    };

    return (
      <>
        <Dialog
          open={open}
          title={
            isCreateMode
              ? translate("emailCycles.allowedDomainsAddDomain")
              : translate("emailCycles.allowedDomainsEditDomain")
          }
          primaryButtonText={
            isCreateMode
              ? translate("emailCycles.allowedDomainsSaveDomain")
              : translate("emailCycles.allowedDomainsUpdateDomain")
          }
          onPrimaryButtonClick={handlePrimaryButtonClick}
          isPrimaryButtonDisabled={activeStore.disableSubmit}
          secondaryButtonText={t("common.cancel")}
          onSecondaryButtonClick={onClose}
          isSecondaryButtonDisabled={
            isCreateMode
              ? createAllowedDomainStore.loadingCreateAllowedDomain
              : updateAllowedDomainStore.loadingUpdateAllowedDomain
          }
          disableBackdropClick={true}
        >
          <AllowedDomainForm
            domainName={activeStore.domainName}
            domain={activeStore.domain}
            disableForm={
              isCreateMode
                ? createAllowedDomainStore.loadingCreateAllowedDomain
                : updateAllowedDomainStore.loadingUpdateAllowedDomain
            }
          />
        </Dialog>

        {openUnexpectedErrorDialog && (
          <UnexpectedErrorDialog
            message={translate(
              isCreateMode
                ? "emailCycles.allowedDomainsCreateUnexpectedErrorMessage"
                : "emailCycles.allowedDomainsUpdateUnexpectedErrorMessage",
            )}
            onClickDone={() => setOpenUnexpectedErrorDialog(false)}
          />
        )}
      </>
    );
  },
);
